// 全局购物车请求队列，确保所有 /cart/change 请求串行执行，防止429
window._cartRequestQueue = window._cartRequestQueue || {
  _running: false,
  _queue: [],
  add(fn) {
    return new Promise((resolve, reject) => {
      this._queue.push({ fn, resolve, reject });
      this._run();
    });
  },
  _run() {
    if(this._running || this._queue.length === 0) return;
    this._running = true;
    const { fn, resolve, reject } = this._queue.shift();
    fn().then(resolve).catch(reject).finally(() => {
      this._running = false;
      this._run();
    });
  }
};

/**
 * 删除购物车 Item
 */
if(!customElements.get('cart-remove')) {
  customElements.define('cart-remove', class CartRemove extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('click', (event) => {
        event.preventDefault();

        const cartItems = this.closest('cart-items');
        if(cartItems) cartItems.updateQuantity(this.dataset.index, 0);
      });
    }
  });
}

if(!customElements.get('cart-items')) {
  customElements.define('cart-items', class CartItems extends HTMLElement {
    constructor() {
      super();

      this._changeTimer = null;   // 防抖计时器
      this._isUpdating = false;   // 并发锁
      this._pendingUpdate = null; // 待处理的更新（并发时暂存）
      this._pendingChange = null; // 待发送的最新变更值

      // 监听修改事件（去掉throttle，由onChange内部防抖控制）
      this.debouncedOnChange = this.onChange.bind(this);
      this.addEventListener('change', this.debouncedOnChange);

      this.sectionId = this.dataset.section;
      this.inDrawer = this.hasAttribute('data-in-drawer');
      if(this.inDrawer) {
        this.drawerTrap = this.closest('[data-trap]');
      }

      this.lineItemStatusElement = document.getElementById(`Cart-Line-Item-Status-${this.sectionId}`);
      this.cartItemsContainer = document.getElementById(`Cart-Items-${this.sectionId}`);

      // 判断礼品包装数量和item数量是否一致
      if(this.hasAttribute('data-gift-wrap-id')) {
        if(parseInt(this.dataset.giftWrapQuantity) !== parseInt(this.dataset.itemQuantity)) {
          setTimeout(() => {
            this.updateQuantity(this.dataset.giftWrapIndex, this.dataset.itemQuantity, null, this.dataset.giftWrapId);
          }, 0);
        }
      }
    }

    /**
     * 元素从DOM移除时，清理事件监听器和计时器
     */
    disconnectedCallback() {
      this.removeEventListener('change', this.debouncedOnChange);
      if(this._changeTimer) clearTimeout(this._changeTimer);
      this._pendingChange = null;
    }

    /**
     * 处理变化事件
     * 防抖：每次点击立即捕获最新值，停止操作300ms后才发请求
     * @param event
     */
    onChange(event) {
      const index = event.target.dataset.index;
      const value = event.target.value;
      const variantId = event.target.dataset.quantityVariantId;

      this._pendingChange = { index, value, variantId };

      if(this._changeTimer) clearTimeout(this._changeTimer);
      this._changeTimer = setTimeout(() => {
        if(!this._pendingChange) return;
        const { index, value, variantId } = this._pendingChange;
        this._pendingChange = null;
        this.updateQuantity(index, value, null, variantId);
      }, 300);
    }

    /**
     * 更新购物车数量
     * 并发锁：请求进行中时，记录最新值，等请求完成后再发，不丢失操作
     * 全局队列：通过 window._cartRequestQueue 串行执行，防止外部脚本并发触发429
     * @param line cart-item Id
     * @param quantity 数量
     * @param focusableElement 可聚焦元素对象
     * @param variantId 变体id
     */
    updateQuantity(line, quantity, focusableElement, variantId) {
      // 并发锁：如果有请求正在进行，记录最新值等待执行
      if(this._isUpdating) {
        this._pendingUpdate = { line, quantity, focusableElement, variantId };
        return;
      }
      this._isUpdating = true;

      this.enableLoading(line);

      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      // 通过全局队列串行执行，防止与外部脚本（如Shop Pay）并发触发429
      window._cartRequestQueue.add(() =>
        fetch(window['routes']['cart_change_url'], { ...fetchConfig(), ...{ body } })
      )
          .then((response) => {
            return response.json();
          })
          .then((responseJson) => {
            if (responseJson.errors) {
              this.restoreLineItemQuantityInputs(line);
              return this.showError(responseJson.errors);
            }

            const updatedValue = responseJson.items[line - 1] ? responseJson.items[line - 1].quantity : undefined;
            let message = '';
            const items = this.querySelectorAll('.cart-item');

            if (items.length === responseJson.items.length && updatedValue !== parseInt(quantity)) {
              if (typeof updatedValue === 'undefined') {
                message = window['cartStrings']['error'];
              } else {
                message = window['cartStrings']['quantityError'].replace('[quantity]', updatedValue);
              }
              this.restoreLineItemQuantityInputs(line);
              return this.showError(message);
            }

            const giftWrapLine = this.dataset.giftWrapIndex;
            const giftWrapping = document.getElementById(`Cart-Gift-Wrapping-${this.sectionId}`);

            if(giftWrapping && giftWrapLine != null && giftWrapLine !== line) {
              const item_quantity = responseJson['item_count'] - parseInt(this.dataset.giftWrapQuantity);
              if(item_quantity > 0) {
                this.updateQuantity(giftWrapLine, item_quantity, focusableElement, variantId);
              } else {
                giftWrapping.removeWrapping();
              }
            } else {
              this.handleCartUpdate(responseJson, variantId, focusableElement);
            }
          }).catch((error) => {
            this.showError(window['cartStrings']['error']);
          }).finally(() => {
            this.disableLoading(line);
            this._isUpdating = false; // 释放并发锁

            // 如果有待处理的更新，执行它
            if(this._pendingUpdate) {
              const pending = this._pendingUpdate;
              this._pendingUpdate = null;
              this.updateQuantity(pending.line, pending.quantity, pending.focusableElement, pending.variantId);
            }
          });
    }

    /**
     * 处理变化后的更新
     * @param responseJson
     * @param variantId
     * @param focusableElement
     */
    handleCartUpdate(responseJson, variantId, focusableElement) {
      SectionDynamicUpdate.updateSections(this.getSectionsToRender(), responseJson.sections);

      if(this.drawerTrap) {
        trapFocus(this.drawerTrap, focusableElement);
      } else if(focusableElement) {
        focusableElement.focus();
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        productVariantId: variantId,
        cartData: responseJson,
      });
    }

    /**
     * 还原 Line Item 中的所有输入框的值
     */
    restoreLineItemQuantityInputs(line) {
      if(!line) return;

      const quantityInputs = this.querySelectorAll(`.quantity-input[data-index='${line}']`);
      quantityInputs.forEach(input => {
        input.value = input.getAttribute('value');
      });
    }

    /**
     * 返回需要更新的Sections
     */
    getSectionsToRender() {
      const sections = [
        {
          id: 'Cart-Icon-Bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        }
      ];

      if(this.inDrawer) {
        sections.push({
          id: 'Cart-Drawer',
          section: this.sectionId,
          selector: '#Cart-Drawer-Details',
        });
      } else {
        sections.push({
          id: 'Main-Cart',
          section: this.sectionId,
          selector: '#Main-Cart-Details',
        });
      }

      return sections;
    }

    /**
     * 显示错误提示
     */
    showError(message = null) {
      if(!message) return;
      popToast(message, 'error');
    }

    /**
     * Loading 状态
     */
    enableLoading(line = null) {
      this.cartItemsContainer.classList.add('cart-items--disabled');

      if(line) {
        const cartItemElement = this.querySelector(`#Cart-Item-${this.sectionId}-${line}`);
        if(!cartItemElement) return;
        cartItemElement.classList.add('loading');
      }

      document.activeElement.blur();
      this.lineItemStatusElement.setAttribute('aria-hidden', "false");
    }

    /**
     * End Loading 状态
     */
    disableLoading(line = null) {
      this.cartItemsContainer.classList.remove('cart-items--disabled');

      if(line) {
        const cartItemElement = this.querySelector(`#Cart-Item-${this.sectionId}-${line}`);
        if(!cartItemElement) return;
        cartItemElement.classList.remove('loading');
      }

      this.lineItemStatusElement.setAttribute('aria-hidden', "true");
    }
  });
}

if(!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', class FreeShippingProgress extends HTMLElement {
    constructor() {
      super();
      this.renderProgress();
    }

    get progressBar() {
      return this.querySelector('.free-shipping-progress');
    }

    get messageWrapper() {
      return this.querySelector('.free-shipping-message');
    }

    renderProgress() {
      const cartTotalAmount = this.dataset.totalAmount;
      const freeThreshold = this.dataset.freeThreshold;

      if(!isNumeric(cartTotalAmount) || !isNumeric(freeThreshold)) return;

      const freeThresholdLocal = Math.round(parseInt(freeThreshold) * (Shopify.currency.rate || 1)) * 100;

      let progress;
      if(cartTotalAmount - freeThresholdLocal >= 0) {
        progress = 100;
        this.messageWrapper.innerHTML = window['cartStrings']['freeShippingUnlockedMessage'];
        this.classList.add('free-has-unlocked');
      } else {
        progress = cartTotalAmount * 100 / freeThresholdLocal;
        const amountToQualify = freeThresholdLocal - cartTotalAmount;
        this.messageWrapper.innerHTML = window['cartStrings']['freeShippingLockedMessage'].replace('[amount]', formatPriceAmount(amountToQualify, Shopify.currency.active));
      }

      if(this.progressBar) this.progressBar.style.setProperty('--free-shipping-progress', `${progress}%`);

      if(this.hasAttribute('data-confetti')) {
        document.dispatchEvent(new CustomEvent('freeShippingUnlocked', {
          detail: { status: progress >= 100 }
        }));
      }
    }
  });
}

/**
 * 订单备注
 */
if (!customElements.get('cart-note-modal')) {
  customElements.define(
      'cart-note-modal',
      class CartNoteModal extends ModalDialog {
        constructor() {
          super();

          const form = this.querySelector('form');
          if(!form) return;

          this.submitButton = this.querySelector('button[type=submit]');
          form.addEventListener('submit', this.formSubmit.bind(this));
        }

        formSubmit(event) {
          event.preventDefault();
          this.startLoading();

          const formData = new FormData(event.target);
          const formDataJson = Object.fromEntries(formData.entries());
          const body = JSON.stringify(formDataJson);

          fetch(window['routes']['cart_update_url'], { ...fetchConfig(), ...{ body } })
              .then(response => response.json())
              .then(response => {
                if(response.error) throw new Error('Cart update error');
                popToast(window['cartStrings']['addToNoteSuccess'], 'success');
                this.hide();
              }).catch(error => {
                popToast(window['cartStrings']['addToNoteError'], 'error');
              }).finally(() => {
                this.endLoading();
              });
        }

        startLoading() {
          this.submitButton.classList.add('loading');
          this.submitButton.setAttribute('disabled', 'disabled');
        }

        endLoading() {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('disabled');
        }
      }
  );
}

/**
 * 订单运费预估
 */
if(!customElements.get('shipping-calculator-modal')) {
  customElements.define('shipping-calculator-modal', class ShippingCalculatorModal extends ModalDialog {

    static MAX_ATTEMPTS = 3;

    constructor() {
      super();

      this.asyncAttemptCount = 0;

      const form = this.querySelector('form');
      if(!form) return;

      form.addEventListener('submit', this.prepareRate.bind(this));

      this.submitButton = this.querySelector('button[type=submit]');
      this.successMessageElement = this.querySelector('.alert-success');
    }

    prepareRate(event) {
      event.preventDefault();
      this.hideResults();
      this.startLoading();

      const formData = new FormData(event.target);
      const shipping_address = {
        country: formData.get('country'),
        province: formData.get('province'),
        zip: formData.get('zip')
      };
      const body = JSON.stringify({ shipping_address });

      fetch(`${window['routes']['cart_url']}/prepare_shipping_rates.json`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            if(!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
            return response.json();
          })
          .then((response) => {
            if(response == null || response.ok) {
              this.asyncRate({ shipping_address });
            } else {
              throw new Error('Prepare shipping rates error');
            }
          }).catch(error => {
            this.showError();
          });
    }

    asyncRate(params) {
      fetch(`${window['routes']['cart_url']}/async_shipping_rates.json?` + this.serialize(params), { ...fetchConfig('json', 'GET') })
          .then((response) => response.json())
          .then((response) => {
            if(response != null && response['shipping_rates'] != null && response['shipping_rates'].length > 0) {
              this.showResults(response);
            } else {
              if(++this.asyncAttemptCount >= ShippingCalculatorModal.MAX_ATTEMPTS) {
                this.showError();
              } else {
                setTimeout(() => this.asyncRate(params), 3000);
              }
            }
          }).catch(error => {
            this.showError();
          });
    }

    startLoading() {
      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('disabled', 'disabled');
    }

    endLoading() {
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('disabled');
    }

    showError() {
      this.endLoading();
      popToast(window['cartStrings']['estimateShippingError'], 'error');
    }

    showResults(result) {
      this.endLoading();

      const rates = result['shipping_rates'];
      if(!rates || rates.length <= 0 || !this.successMessageElement) return;

      const countElement = this.successMessageElement.querySelector('.result-count');
      countElement.innerText = rates.length;

      const template = this.successMessageElement.querySelector('template').content;
      const fragment = document.createDocumentFragment();
      rates.forEach((rate) => {
        const clone = document.importNode(template, true);
        clone.querySelector('.presentment-name').textContent = rate['presentment-name'] || rate['name'];
        clone.querySelector('.delivery-price').textContent = rate['price'] + rate['currency'];

        let deliveryDate = rate['delivery_date'];
        if(rate['delivery_range'] && Array.isArray(rate['delivery_range'])) {
          deliveryDate = rate['delivery_range'].length > 1
              ? rate['delivery_range'].join(' - ')
              : rate['delivery_range'][0];
        }
        if(deliveryDate == null || deliveryDate === '') deliveryDate = 'unknown';

        clone.querySelector('.delivery-date').textContent = deliveryDate;
        fragment.appendChild(clone);
      });

      this.successMessageElement.querySelector('.message-list').appendChild(fragment);
      this.successMessageElement.removeAttribute('hidden');
      this.endLoading();
    }

    hideResults() {
      if(this.successMessageElement) {
        this.successMessageElement.querySelector('.message-list').innerHTML = '';
        this.successMessageElement.setAttribute('hidden', 'true');
      }
    }

    serialize(obj, prefix) {
      const str = Object.keys(obj)
          .filter(key => obj.hasOwnProperty(key))
          .map(key => {
            const k = prefix ? `${prefix}[${key}]` : key;
            const v = obj[key];
            return (v !== null && typeof v === "object")
                ? this.serialize(v, k)
                : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
          });
      return str.join("&");
    }
  });
}

/**
 * 礼品包装
 */
if(!customElements.get('gift-wrapping')) {
  customElements.define('gift-wrapping', class GiftWrapping extends HTMLElement {
    constructor() {
      super();

      this.giftWrapId = this.dataset.giftWrappingId;
      if(!this.giftWrapId) return;
      this.itemQuantity = Math.max(0, parseInt(this.dataset.itemQuantity));

      this.cartItems = document.getElementById(`Cart-Items-Main-${this.dataset.section}`);

      const boundChange = debounce(this.onChange.bind(this), 1000);
      this.checkInput = this.querySelector('input[type="checkbox"]');
      this.checkInput.addEventListener('change', boundChange);
    }

    onChange(event) {
      if(event.target.checked) {
        this.setWrapping();
      } else {
        this.removeWrapping();
      }
    }

    setWrapping() {
      const body = JSON.stringify({
        updates: { [this.giftWrapId]: this.itemQuantity },
        attributes: { "gift-wrap": "Yes" },
        sections: this.cartItems.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });
      this.fetchData(body);
    }

    removeWrapping() {
      const body = JSON.stringify({
        updates: { [this.giftWrapId]: 0 },
        attributes: { "gift-wrap": "" },
        sections: this.cartItems.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });
      this.fetchData(body);
    }

    fetchData(body) {
      this.enableLoading();
      fetch(window['routes']['cart_update_url'], { ...fetchConfig(), body })
          .then(response => response.json())
          .then(responseJson => {
            this.cartItems.handleCartUpdate(responseJson, this.giftWrapId, this.checkInput);
          }).catch(error => {
            popToast(window['variantStrings']['unknownError'], 'error');
          }).finally(() => {
            this.disableLoading();
          });
    }

    enableLoading() {
      this.cartItems.enableLoading();
      this.checkInput.setAttribute('disabled', 'true');
    }

    disableLoading() {
      this.cartItems.disableLoading();
      this.checkInput.removeAttribute('disabled');
    }
  });
}
